// Runs before the rest of the app
import * as Sentry from "@sentry/react";
import { LicenseInfo } from "@mui/x-license";

const hostname = window.location.hostname;
const RESIZE_OBSERVER_ERROR_MESSAGES = new Set([
  "ResizeObserver loop completed with undelivered notifications.",
  "ResizeObserver loop limit exceeded",
]);
const RESIZE_OBSERVER_FILTER_FLAG =
  "__mapsResizeObserverErrorFilterInstalled__";
const APPRAISAL_NOTE_SENSITIVE_KEYS = new Set([
  "note",
  "currentNote",
  "currentValue",
  "structuredNotes",
  "appraiseeNotes",
  "appraiserNotes",
  "goalNotes",
  "additionalNotes",
  "secondAppraiseeNotes",
  "secondAppraiserNotes",
  "secondGoalNotes",
  "secondAdditionalNotes",
  "fileName",
  "filename",
  "fileKey",
  "sourceUrl",
]);
const AUTHENTICATION_QUERY_SECRET_PATTERN =
  /([?&#](?:joinCode|credential|participantCredential|code|state|session_state|token|activationToken|authenticationInvitation|linkToken|continuationToken|exchangeToken|preAuthToken|enrollmentToken|confirmationToken|backupCodeConfirmationToken|id_token|access_token|refresh_token|assertion|SAMLResponse)=)[^&#]*/gi;
const SURVEY_CREDENTIAL_PATH_PATTERN = /(\/(?:surveys\/(?:live-public|programme-public|external)|external-surveys|s|survey)\/)[^/?#\s]+/gi;
const isProgrammePage = (event) => /\/(?:survey\/|surveys\/(?:programme-public|programmes|trust-reports)(?:[/?#]|$))/i.test(`${event.request?.url || ""} ${event.transaction || ""} ${window.location?.pathname || ""}`);
const filterProgrammeContext = (event) => {
  delete event.extra;
  if (event.contexts) event.contexts = event.contexts.trace ? { trace: Object.fromEntries(Object.entries(event.contexts.trace)
    .filter(([key]) => ["trace_id", "span_id", "parent_span_id", "op", "status"].includes(key))) } : {};
  if (event.spans) event.spans = event.spans.map((span) => ({
    ...Object.fromEntries(Object.entries(span).filter(([key]) => ["op", "span_id", "parent_span_id", "trace_id", "start_timestamp", "timestamp", "status"].includes(key))),
    description: "Survey participation operation", data: { filtered: true },
  }));
};
const AUTHENTICATION_SECRET_KEYS = new Set([
  "authorization",
  "joincode",
  "credential",
  "credentialhash",
  "participantcredential",
  "liveparticipanthash",
  "answers",
  "cookie",
  "set-cookie",
  "code",
  "backupcode",
  "backupcodes",
  "confirmationtoken",
  "preauthtoken",
  "exchangetoken",
  "authenticationinvitation",
  "linktoken",
  "continuationtoken",
  "continuationhash",
  "emailchangebrowserhash",
  "emailchangenonce",
  "state",
  "assertion",
  "password",
  "currentpassword",
  "newpassword",
  "secret",
  "clientsecret",
  "clientsecretencrypted",
  "token",
  "tokenhash",
  "activationtoken",
  "enrollmenttoken",
  "backupcodeconfirmationtoken",
  "accesstoken",
  "refreshtoken",
  "refreshtokenhash",
  "idtoken",
  "id_token",
  "mfasecret",
  "pendingtotpsecret",
  "pendingbackupcodes",
  "replacementtotpsecret",
  "replacementbackupcodes",
  "replacementcode",
  "mfatempsecret",
  "mfabackupcodes",
  "samlresponse",
]);

// enable Sentry only for real client deployments
const isProductionDomain = !["localhost"].includes(hostname);

function getResizeObserverErrorMessage(value) {
  if (typeof value === "string") return value;
  if (typeof value?.message === "string") return value.message;
  if (typeof value?.reason?.message === "string") return value.reason.message;
  if (typeof value?.error?.message === "string") return value.error.message;
  return "";
}

function isIgnorableResizeObserverError(value) {
  return RESIZE_OBSERVER_ERROR_MESSAGES.has(
    getResizeObserverErrorMessage(value),
  );
}

function scrubAppraisalNoteContent(value, seen = new WeakSet()) {
  if (!value || typeof value !== "object") return value;
  if (seen.has(value)) return value;
  seen.add(value);

  if (Array.isArray(value)) {
    value.forEach((entry) => scrubAppraisalNoteContent(entry, seen));
    return value;
  }

  Object.entries(value).forEach(([key, entry]) => {
    if (APPRAISAL_NOTE_SENSITIVE_KEYS.has(key)) {
      value[key] = "[Filtered]";
    } else {
      scrubAppraisalNoteContent(entry, seen);
    }
  });
  return value;
}

function redactAuthenticationSecrets(value, seen = new WeakSet()) {
  if (typeof value === "string") {
    return value.replace(AUTHENTICATION_QUERY_SECRET_PATTERN, "$1[redacted]")
      .replace(SURVEY_CREDENTIAL_PATH_PATTERN, "$1[redacted]");
  }
  if (!value || typeof value !== "object" || seen.has(value)) return value;
  seen.add(value);

  if (Array.isArray(value)) {
    value.forEach((entry, index) => {
      const redacted = redactAuthenticationSecrets(entry, seen);
      if (redacted !== entry) value[index] = redacted;
    });
    return value;
  }

  Object.entries(value).forEach(([key, entry]) => {
    if (AUTHENTICATION_SECRET_KEYS.has(key.toLowerCase())) {
      try {
        value[key] = "[Filtered]";
      } catch {
        // Some SDK metadata is exposed through getter-only properties.
      }
      return;
    }
    const redacted = redactAuthenticationSecrets(entry, seen);
    if (redacted !== entry) {
      try {
        value[key] = redacted;
      } catch {
        // Leave getter-only SDK metadata untouched.
      }
    }
  });
  return value;
}

function isAppraisalPage(url = "") {
  const location = `${url} ${window.location?.pathname || ""}`;
  return /\/(?:appraisal|meeting-notes)(?:[/?#]|$)/i.test(location);
}

function isFileStorageRequest(url = "") {
  return /\/getUploadLink(?:[/?#]|$)/i.test(String(url || ""));
}

function isAuthenticationRequest(url = "") {
  return /\/auth(?:[/?#]|$)/i.test(String(url || ""));
}

function isLiveSurveyPage(url = "") {
  return /\/(?:s\/|survey\/|surveys\/(?:live-(?:public|sessions)|programme-public|programmes|trust-reports)(?:[/?#]|$)|surveys\/live\/)/i.test(
    `${url} ${window.location?.pathname || ""}`,
  );
}

function suppressBenignResizeObserverErrors() {
  if (typeof window === "undefined") return;
  if (window[RESIZE_OBSERVER_FILTER_FLAG]) return;

  window.addEventListener("error", (event) => {
    if (!isIgnorableResizeObserverError(event)) return;
    event.preventDefault?.();
    event.stopImmediatePropagation?.();
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (!isIgnorableResizeObserverError(event)) return;
    event.preventDefault?.();
    event.stopImmediatePropagation?.();
  });

  window[RESIZE_OBSERVER_FILTER_FLAG] = true;
}

suppressBenignResizeObserverErrors();

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  sendDefaultPii: false,
  integrations: [Sentry.browserTracingIntegration()],
  // Replay is deliberately not installed. Keep both sampling paths disabled,
  // including public Live Sessions reached through client-side navigation.
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,
  tracesSampleRate: 0.15,

  // use the client hostname as the environment label
  environment: hostname,
  release: process.env.REACT_APP_SENTRY_RELEASE,

  //   only send events from production client domains
  enabled: isProductionDomain,

  beforeSend(event) {
    if (isProgrammePage(event)) filterProgrammeContext(event);
    const errorMessage =
      event?.exception?.values?.[0]?.value ||
      event?.message ||
      event?.logentry?.formatted ||
      "";
    if (isIgnorableResizeObserverError(errorMessage)) return null;
    // scrub sensitive info
    if (event.user) delete event.user.email;
    const appraisalPage = isAppraisalPage(event.request?.url);
    const fileStorageRequest = isFileStorageRequest(event.request?.url);
    const authenticationRequest = isAuthenticationRequest(event.request?.url);
    const liveSurveyPage = isLiveSurveyPage(event.request?.url);
    scrubAppraisalNoteContent(event.request);
    scrubAppraisalNoteContent(event.extra);
    scrubAppraisalNoteContent(event.contexts);
    scrubAppraisalNoteContent(event.breadcrumbs);
    if (authenticationRequest && event.request?.data !== undefined) {
      event.request.data = "[Filtered]";
    }
    redactAuthenticationSecrets(event);
    if (appraisalPage || fileStorageRequest || liveSurveyPage) {
      if (liveSurveyPage) delete event.user;
      if (event.request?.data) event.request.data = "[Filtered]";
      const safeMessage = liveSurveyPage ? "Live survey client error" : appraisalPage
        ? "Appraisal client error"
        : "File storage client error";
      if (event.message) event.message = safeMessage;
      if (event.logentry?.formatted) {
        event.logentry.formatted = safeMessage;
      }
      if (liveSurveyPage && event.logentry?.message) event.logentry.message = safeMessage;
      for (const exception of event.exception?.values || []) {
        if (exception?.value) exception.value = safeMessage;
      }
      for (const breadcrumb of event.breadcrumbs || []) {
        breadcrumb.message = liveSurveyPage ? "Live survey interaction" : "Appraisal interaction";
        breadcrumb.data = { filtered: true };
      }
    }
    return event;
  },
  beforeBreadcrumb(breadcrumb) {
    scrubAppraisalNoteContent(breadcrumb);
    redactAuthenticationSecrets(breadcrumb);
    if (isAppraisalPage() || isLiveSurveyPage()) {
      breadcrumb.message = isLiveSurveyPage() ? "Live survey interaction" : "Appraisal interaction";
      breadcrumb.data = { filtered: true };
    }
    return breadcrumb;
  },
  beforeSendTransaction(event) {
    if (isProgrammePage(event)) filterProgrammeContext(event);
    if (isLiveSurveyPage(`${event.request?.url || ""} ${event.transaction || ""}`)) {
      delete event.user;
      if (event.request?.data !== undefined) event.request.data = "[Filtered]";
    }
    redactAuthenticationSecrets(event);
    return event;
  },
  denyUrls: [/extensions\//i, /chrome(-extension)?:\/\//i],
});

// Must be set before any MUI X components are imported anywhere
LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_X_LICENCE_KEY);
